
import { Vue, Component, Prop } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import BarcodeOrderModule from "@/store/modules/barcodeSystem/barcodeOrder";
import StockSkuModule from "@/store/modules/barcodeSystem/stockSku";
import BarcodeManufacturerModule from "@/store/modules/barcodeSystem/barcodeManufacturer";
import { BarcodeOrderResource } from "@/models/api/barcodeSystem/barcodeOrders/get";
import { RequiredStockItemResource } from "@/models/api/barcodeSystem/barcodeOrders/requiredStock";
import { StockSkuResource } from "@/models/api/barcodeSystem/stockSkus/get";
import notifications from "@/lib/notifications";

const barcodeOrderModuleState = getModule(BarcodeOrderModule);
const stockSkuModuleState = getModule(StockSkuModule);
const barcodeManufacturerModuleState = getModule(BarcodeManufacturerModule);

@Component({})
export default class AddRequiredStockModal extends Vue {
  @Prop({ default: false })
  value: boolean;

  @Prop()
  barcodeOrderId: number;

  loading: boolean = true;
  saving: boolean = false;
  barcodeOrder: BarcodeOrderResource = null;
  items: RequiredStockItemResource[] = [];

  showSkuPickerModal: boolean = false;
  skuSearchFilter: string = null;

  showSkuDetailModal: boolean = false;
  selectedSkuForDetail: StockSkuResource = null;
  detailQuantity: number = 1;
  detailPoNumber: string = null;
  detailSerialNumber: string = null;
  addedStockSkuIds: number[] = [];

  async loadData() {
    this.loading = true;
    this.addedStockSkuIds = [];
    try {
      this.barcodeOrder = await barcodeOrderModuleState.getBarcodeOrder(this.barcodeOrderId);
      this.items = await barcodeOrderModuleState.getRequiredStock(this.barcodeOrderId) || [];
      if (this.barcodeOrder && this.barcodeOrder.company) {
        await stockSkuModuleState.loadStockSkus({ companyId: this.barcodeOrder.company.id });
      }
    } catch (ex) {
      notifications.error("Add Required Stock", "Failed to load order details", ex);
    }
    this.loading = false;
  }

  get availableStockSkus(): StockSkuResource[] {
    const skus = stockSkuModuleState.stockSkus || [];
    const filterText = (this.skuSearchFilter || '').toUpperCase().trim();
    const allowedManufacturerCodes = (barcodeManufacturerModuleState.manufacturers || [])
      .filter(x => x.allowManualStockOrdersCreation)
      .map(x => x.code);

    return skus.filter(x => allowedManufacturerCodes.includes(x.manufacturerCode) && (!filterText ||
      x.sku.toUpperCase().includes(filterText) ||
      (x.itemDescription && x.itemDescription.toUpperCase().includes(filterText)) ||
      ((x.commodity || x.itemType) && (x.commodity || x.itemType).toUpperCase().includes(filterText))));
  }

  openSkuPickerModal() {
    this.skuSearchFilter = null;
    this.showSkuPickerModal = true;
  }

  selectSku(sku: StockSkuResource) {
    this.selectedSkuForDetail = sku;
    this.detailQuantity = 1;
    this.detailPoNumber = null;
    this.detailSerialNumber = null;
    this.showSkuPickerModal = false;
    this.showSkuDetailModal = true;
  }

  backToSkuPicker() {
    this.showSkuDetailModal = false;
    this.showSkuPickerModal = true;
  }

  cancelSkuDetail() {
    this.showSkuDetailModal = false;
    this.selectedSkuForDetail = null;
  }

  addSelectedSkuToOrder() {
    const sku = this.selectedSkuForDetail;
    if (!sku || !this.detailQuantity || this.detailQuantity < 1) return;

    this.items.push({
      stockSkuId: sku.id,
      facilityCode: this.barcodeOrder ? this.barcodeOrder.facilityCode : null,
      companyId: sku.company ? sku.company.id : sku.companyId,
      companyName: sku.company ? sku.company.companyName : (sku.companyName || (this.barcodeOrder && this.barcodeOrder.company ? this.barcodeOrder.company.companyName : null)),
      manufacturerCode: sku.manufacturer ? sku.manufacturer.code : sku.manufacturerCode,
      manufacturerName: sku.manufacturer ? sku.manufacturer.manufacturerName : sku.manufacturerName,
      commodity: sku.commodity || sku.itemType,
      modelNumber: sku.modelNumber || null,
      sku: sku.sku,
      itemDescription: sku.itemDescription,
      quantity: this.detailQuantity,
      poNumber: sku.isBulkItem ? null : this.detailPoNumber,
      serialNumber: sku.isBulkItem ? null : this.detailSerialNumber
    });
    this.addedStockSkuIds.push(sku.id);

    this.showSkuDetailModal = false;
    this.selectedSkuForDetail = null;
  }

  addedViaAddStock(stockSkuId: number): boolean {
    return this.addedStockSkuIds.includes(stockSkuId);
  }

  removeItem(index: number) {
    this.items.splice(index, 1);
  }

  async saveRequiredStock() {
    this.saving = true;
    try {
      await barcodeOrderModuleState.saveRequiredStock({
        orderId: this.barcodeOrderId,
        items: this.items
      });
      notifications.success("Required Stock Saved", "Successfully saved required stock items");
    } catch (ex) {
      notifications.error("Save Required Stock", "Failed to save required stock items", ex);
    }
    this.saving = false;
  }

  closeModal() {
    this.$emit('input', false);
  }
}
